import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'

import {
  useOktaUserQuery,
  useDownloadPiiReportMutation
} from '../../graphql/graphql'
import { useAccessLevel } from '../../context/AccessLevelContext'
import { AppFunction } from '../../auth/roles'

import 'primereact/resources/themes/lara-light-blue/theme.css'
import 'primereact/resources/primereact.min.css'
import 'primeicons/primeicons.css'

interface PIITableProps {
  email: string
}

export function OktaPIIComponent({ email }: PIITableProps) {
  const { isAuthorized } = useAccessLevel()
  const [searchParams] = useSearchParams()
  const [user, setUser] = useState(email)
  const [pii, setPii] = useState<any[]>([])
  const { data, loading, refetch } = useOktaUserQuery({
    variables: { username: user },
    skip: !user
  })
  const [downloadPiiReportMutation] = useDownloadPiiReportMutation()

  useEffect(() => {
    const query = searchParams.get('email') ?? ''
    setUser(query)
    refetch()
    const profile = data ? data?.oktaUserProfile : undefined
    if (profile && pii.length === 0) {
      const piiObject = [
        { key: 'Username', value: profile.login ?? '' },
        { key: 'Email', value: profile.email ?? '' },
        { key: 'First Name', value: profile.firstName ?? '' },
        { key: 'Last Name', value: profile.lastName ?? '' },
        { key: 'Primary Phone Number', value: profile.primaryPhone ?? '' },
        { key: 'Mobile Phone', value: profile.mobilePhone ?? '' },
        { key: 'Secondary Email', value: profile.secondEmail ?? '' },
        { key: 'Personal Email', value: profile.personal_email ?? '' },
        { key: 'Company', value: profile.company ?? '' },
        { key: 'Title', value: profile.title ?? '' },
        { key: 'Job Level', value: profile.job_function ?? '' },
        { key: 'Job Function', value: profile.job_level ?? '' },
        { key: 'Street Address', value: profile.streetAddress ?? '' },
        { key: 'City', value: profile.city ?? '' },
        { key: 'State', value: profile.state ?? '' },
        { key: 'Zip Code', value: profile.zipCode ?? '' },
        { key: 'Country', value: profile.country ?? '' },
        { key: 'Country Code', value: profile.countryCode ?? '' },
        { key: 'Geo Region', value: profile.geo_region ?? '' },
        { key: 'Geo Country', value: profile.geo_country ?? '' },
        { key: 'Geo Country Code', value: profile.geo_country_code ?? '' },
        { key: 'ip_address', value: profile.ip_address ?? '' },
        { key: 'Latitude', value: profile.latitude ?? '' },
        { key: 'Longitude', value: profile.longitude ?? '' }
      ]
      setPii(piiObject)
    }
  }, [data, pii, refetch, searchParams])

  const convertObjectToCSV = (obj: any) => {
    if (!obj || !obj.oktaUserProfile || typeof obj.oktaUserProfile !== 'object')
      return ''
    const profile = obj.oktaUserProfile
    const piiObject = {
      Username: profile.login,
      Email: profile.email,
      'First Name': profile.firstName,
      'Last Name': profile.lastName,
      'Primary Phone Number': profile.primaryPhone,
      'Mobile Phone': profile.mobilePhone,
      'Secondary Email': profile.secondEmail,
      'Personal Email': profile.personal_email,
      Company: profile.company,
      Title: profile.title,
      'Job Level': profile.job_function,
      'Job Function': profile.job_level,
      'Street Address': profile.streetAddress,
      City: profile.city,
      State: profile.state,
      'Zip Code': profile.zipCode,
      Country: profile.country,
      'Country Code': profile.countryCode,
      'Geo Region': profile.geo_region,
      'Geo Country': profile.geo_country,
      'Geo Country Code': profile.geo_country_code,
      ip_address: profile.ip_address,
      Latitude: profile.latitude,
      Longitude: profile.longitude
    }
    const rows = Object.entries(piiObject).map(
      ([key, value]) => `${JSON.stringify(key)},${JSON.stringify(value)}`
    )
    return `Key,Value\n${rows.join('\n')}`
  }

  const downloadCSV = (csv: any, filename: string = 'data.csv') => {
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }

  const handleDownload = async () => {
    await downloadPiiReportMutation({
      variables: {
        username: user
      }
    })
    if (!data || loading || pii.length === 0) return
    const csv = convertObjectToCSV(data)
    const lastName = data.oktaUserProfile?.lastName ?? 'no_last_name'
    downloadCSV(csv, `SAR_OKTA_${lastName}.csv`)
  }

  if (isAuthorized(AppFunction.PIIReport)) {
    return (
      <div className="card" style={{ paddingBottom: 30 }}>
        <button
          type="button"
          className="btn btn-primary"
          style={{ marginBottom: 10 }}
          onClick={handleDownload}
          disabled={loading || !data}
        >
          <span>Download PII Report</span>
        </button>
        <div>
          <DataTable value={pii} loading={loading} responsiveLayout="scroll">
            {/* eslint-disable-next-line */}
            <Column field="key" header="Key"></Column>
            {/* eslint-disable-next-line */}
            <Column field="value" header="Value"></Column>
          </DataTable>
        </div>
      </div>
    )
  }
  return <div>You do not have access</div>
}
